import { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Drawer, useMediaQuery } from '@mui/material';
import DrawerHeader from './DrawerHeader';
import DrawerContent from './DrawerContent';
import MiniDrawerStyled from './MiniDrawerStyled';
import { DRAWER_WIDTH } from 'config';
import { useDispatch, useSelector } from 'react-redux';
import { openDrawer } from 'store/reducers/menu';
import DrawerFooter from './DrawerFooter';
import { MainDrawerProps } from 'types/layout';
import { RootState } from 'store';

const MainDrawer = ({ window }: MainDrawerProps) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const dispatch = useDispatch();

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const drawerOpenFromStore = useSelector((state: RootState) => state.menu.drawerOpen);

    useEffect(() => {
        setIsDrawerOpen(drawerOpenFromStore);
    }, [drawerOpenFromStore]);

    const handleDrawerToggle = useCallback(() => {
        const newDrawerState = !isDrawerOpen;
        setIsDrawerOpen(newDrawerState);
        dispatch(openDrawer(newDrawerState));
    }, [isDrawerOpen, dispatch]);

    const container = window !== undefined ? () => window().document.body : undefined;

    return (
        <Box component='nav' sx={{ flexShrink: { md: 0 }, zIndex: 1300 }} aria-label='mailbox folders'>
            {isSmallScreen ? (
                <Drawer
                    container={container}
                    variant='temporary'
                    open={isDrawerOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', lg: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: DRAWER_WIDTH,
                            borderRight: `1px solid ${theme.palette.divider}`,
                            backgroundImage: 'none',
                            boxShadow: 'inherit',
                        },
                    }}>
                    <DrawerHeader open={isDrawerOpen} />
                    <DrawerContent />
                    <DrawerFooter open={isDrawerOpen} />
                </Drawer>
            ) : (
                <MiniDrawerStyled variant='permanent' open={isDrawerOpen}>
                    <DrawerHeader open={isDrawerOpen} />
                    <DrawerContent />
                    <DrawerFooter open={isDrawerOpen} />
                </MiniDrawerStyled>
            )}
        </Box>
    );
};

export default MainDrawer;
